import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Heading,
  Text,
} from '@chakra-ui/react';
import { ChevronRightIcon, QuestionIcon } from '@udacity/chakra-uds-icons';

import { ContentContainer } from '~/components/container';
import { Link } from '~/components/link';
import { trackEvent } from '~/features/analytics/track-event';
import { shouldSeeAnnualBundlePlan } from '~/features/checkout/utils/should-see-annual-plan';
import { useCurrency } from '~/features/internationalization/hooks/use-currency';

import { PlansStatsSection } from './plans-stats-section';

// The bundle FAQ question and answer match the plan the pricing hero surfaces
// for the viewer's currency (annual in `ANNUAL_PRICE_CURRENCIES`, 4-month
// elsewhere). Each answer describes only its own bundle.
function getFaqs(isAnnualBundle: boolean) {
  return [
    {
      question: 'What is a Nanodegree program?',
      answer:
        'A Nanodegree program is a project-based learning experience built around the skills employers need. You learn through lessons, hands-on projects, expert feedback, and career resources.',
    },
    {
      question: 'What is included in my subscription?',
      answer:
        'Your subscription includes unlimited access to Udacity courses and Nanodegree programs, real-world projects, personalized feedback, career services, and certificates when you complete eligible programs.',
    },
    {
      question: 'Can I cancel my subscription?',
      answer:
        'Yes. You can cancel anytime from your account settings. You will keep access until the end of your current billing period.',
    },
    {
      question: isAnnualBundle ? 'How does the annual bundle work?' : 'How does the 4-month bundle work?',
      answer: isAnnualBundle
        ? 'The annual bundle gives you access for a full year at a discounted monthly rate, so you can keep learning at the best value, after which plans are converted to month-to-month.'
        : 'The 4-month bundle gives you access for four months at a discounted monthly rate, based on the average time learners take to complete a Nanodegree program, after which plans are converted to month-to-month.',
    },
    {
      question: 'Do I get a certificate?',
      answer:
        'Yes. You can earn a certificate after completing all required projects and lessons in an eligible program.',
    },
    {
      question: 'Can I switch plans?',
      answer:
        'You can manage your subscription and billing options from your account. Available plan changes may vary by region and active promotions.',
    },
  ];
}

export function PlansStatsFaqSection() {
  const { currency } = useCurrency();
  const faqs = getFaqs(shouldSeeAnnualBundlePlan(currency));

  return (
    <Box as='section' bgColor='white' py={{ base: 16, lg: 24 }}>
      <ContentContainer contentMaxWidth='content-max-width-md'>
        <PlansStatsSection />

        <Box maxW='920px' mx='auto'>
          <Heading
            as='h2'
            fontSize={{ base: '2rem', lg: '3.375rem' }}
            fontWeight='600'
            letterSpacing='-0.04em'
            lineHeight='1.1'
            mb={12}
            textAlign='center'
          >
            Purchase FAQs
          </Heading>
          <Accordion border='none' display='flex' flexDirection='column' gap='8px' allowMultiple>
            {faqs.map((faq) => (
              <AccordionItem key={faq.question} border='none'>
                {({ isExpanded }) => (
                  <>
                    <Heading as='h3'>
                      <AccordionButton
                        _focus={{ boxShadow: 'none' }}
                        _focusVisible={{ outline: '2px solid', outlineColor: 'blue.500', outlineOffset: '-2px' }}
                        _hover={{ bg: isExpanded ? 'grey.50' : 'grey.100' }}
                        bg='grey.50'
                        borderColor='grey.100 !important'
                        borderRadius={isExpanded ? '4px 4px 0 0 !important' : '4px !important'}
                        borderStyle='solid !important'
                        borderWidth={isExpanded ? '1px 1px 0 1px !important' : '1px !important'}
                        px={5}
                        py={4}
                        onClick={() => {
                          if (!isExpanded) {
                            trackEvent({
                              name: 'Plans CTA Clicked',
                              type: 'click',
                              label: 'plans_faq_expanded',
                              value: faq.question,
                            });
                          }
                        }}
                      >
                        <Box
                          color='grey.900'
                          flex='1'
                          fontFamily='body'
                          fontSize='16px'
                          fontWeight='600'
                          textAlign='left'
                        >
                          {faq.question}
                        </Box>
                        <AccordionIcon boxSize='18px' color='grey.900' />
                      </AccordionButton>
                    </Heading>
                    <AccordionPanel
                      bg='grey.50'
                      borderBottomRadius='4px !important'
                      borderColor='grey.100 !important'
                      borderStyle='solid !important'
                      borderWidth='0 1px 1px 1px !important'
                      color='grey.500'
                      fontSize='13px'
                      lineHeight='1.4'
                      px={5}
                      py={6}
                    >
                      {faq.answer}
                    </AccordionPanel>
                  </>
                )}
              </AccordionItem>
            ))}
          </Accordion>
          <Link
            _hover={{ bg: 'blue.100', textDecoration: 'none' }}
            alignItems='center'
            bg='blue.50'
            border='1px solid'
            borderColor='blue.100'
            borderRadius='4px'
            color='grey.900'
            display='flex'
            href='https://support.udacity.com/hc/en-us'
            justifyContent='space-between'
            mt={12}
            px={5}
            py={4}
            textDecoration='none'
            isExternal
            onClick={() =>
              trackEvent({
                name: 'Plans CTA Clicked',
                type: 'click',
                label: 'plans_help_center_clicked',
              })
            }
          >
            <Flex alignItems='center' columnGap={3} flexWrap='wrap' rowGap={1}>
              <QuestionIcon boxSize='24px' color='blue.500' flexShrink={0} />
              <Text fontFamily='heading' fontSize='18px' fontWeight='600' letterSpacing='-0.01em' textDecoration='none'>
                Have additional questions?
              </Text>
              <Text
                color='blue.500'
                fontFamily='heading'
                fontSize='18px'
                fontWeight='600'
                letterSpacing='-0.01em'
                textDecoration='underline'
              >
                Visit our Help Center
              </Text>
            </Flex>
            <ChevronRightIcon boxSize='18px' color='grey.900' flexShrink={0} />
          </Link>
        </Box>
      </ContentContainer>
    </Box>
  );
}
